import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Breadcrumbs,
    CountDown,
    DetailsBanner,
    HowToBanner,
    ViewTemplate,
} from '../..';
import './htv-category-library.scss';
import { useAppInformationContent } from '../../breadcrumbs/hook/use-breadcrumb-content';
import { HowToPopularVideoTiles } from '../popular-videos/how-to-popular-video-tiles';
import { FdsChevron } from '../../../common/chevron/fds-chevron';
import ServerContext from '../../../../contexts/serverContext';
import {
    desktopBackground,
    mobileBackground,
    QUICKTIP_DYF_TITLE,
} from './category-banner-background';
import { RoadBlockPage } from '../../../../views/road-block-page/road-block-view';
import { useAnalytics } from '../../../../hooks/use-analytics';
import { HeadTags } from '../../../common/video/video';
import useSeoConfig from '../../../../hooks/use-seo-config';
import siteMapService from '../../../../support/services/site-map-service/site-map-service';
import PopularVideoService from '../../../../services/onboarding-brightcove-video-service/popular-video-service';
import { useDyfEligibleModel } from '../../../../hooks/dyf/use-dyf-eligible-model/use-dyf-eligible-model';
import { getAemVehicleModelData } from '../../../../hooks/use-aem-vehicle-model-data';
import { useServerData } from '../../../../hooks/use-server-data';
import { PageType } from '../../view-template/page-type';
import { filterXSS } from 'xss';
import { useDyfQueryParams } from '../../../../hooks/dyf/use-dyf-query-params/use-dyf-query-params';
import { useHowToVideoContent } from '../../../../hooks/dyf/use-how-to-video-content/use-how-to-video-content';
import useFooterDisplay from '../../../../hooks/use-footer-display';

export const HTVCategoryLibrary = () => {
    const appInformationContent = useAppInformationContent();
    const howToVideoContent: any = useHowToVideoContent();
    const page = useRef<PageType>('How To Video');

    const serverContext = useContext(ServerContext);
    const {
        brand,
        currentRegionCode,
        currentLanguageRegionCode,
    } = serverContext;

    const [breadcrumbToggle, setBreadCrumbToggle] = useState(false);
    const baseRoot = serverContext.root
        ? serverContext.root.substring(0, serverContext.root.length - 1)
        : '';
    const [fireEvents] = useAnalytics();
    const [analyticsFired, setAnalyticsFired] = useState<boolean>(false);
    const [
        categoryVideosResponseError,
        setCategoryVideosResponseError,
    ] = useState<boolean>(false);

    const { queryYear, queryModel, queryCategory } = useDyfQueryParams();
    const category = queryCategory.replace(/-/gm, ' ').toLowerCase();

    const serverDataCallbackMarketName = async () => {
        const vehicleYmm = await getAemVehicleModelData(
            brand,
            currentRegionCode,
            currentLanguageRegionCode,
            queryYear,
            queryModel,
            'SEO_KEY'
        );
        if (vehicleYmm) return Promise.resolve(vehicleYmm.model);
        else return Promise.resolve('');
    };
    const [marketName] = useServerData(
        'marketName',
        serverDataCallbackMarketName,
        [brand, currentRegionCode, currentLanguageRegionCode]
    );

    const { data: dyfEligible } = useDyfEligibleModel({
        year: queryYear,
        modelAlias: queryModel,
    });

    const serverDataCallback = async () => {
        const vehicleYmm = await getAemVehicleModelData(
            brand,
            currentRegionCode,
            currentLanguageRegionCode,
            queryYear,
            queryModel,
            'SEO_KEY'
        );
        if (vehicleYmm && vehicleYmm.marketName) {
            console.log(
                '--- HTV Server Data Category Library CALL :: htv-category-library.tsx:87 ---'
            );
            const categoryVideos = await new PopularVideoService().getVideosByCategory(
                category,
                queryYear,
                brand,
                vehicleYmm.marketName,
                currentLanguageRegionCode
            );
            if (Object.keys(categoryVideos).length > 0) {
                for (const video in categoryVideos) {
                    categoryVideos[video].seoKey = vehicleYmm.seoKey;
                    categoryVideos[
                        video
                    ].custom_fields.year = vehicleYmm.year.toString();
                    categoryVideos[video].custom_fields.model =
                        vehicleYmm.marketName;
                }
                return Promise.resolve(categoryVideos);
            } else {
                setCategoryVideosResponseError(true);
                return Promise.resolve([]);
            }
        }
    };
    const [categoryVideos] = useServerData(
        'VideoCategoriesResponse',
        serverDataCallback,
        [queryCategory, queryYear, queryModel]
    );

    useEffect(() => {
        if (marketName?.data && !analyticsFired) {
            setAnalyticsFired(true);
            const dyfComponentName = {
                categoryName: `${category}`,
                dyfComponentName: `video library:${category}`,
                ymm: {
                    year: parseInt(queryYear, 10),
                    make: brand,
                    model: marketName?.data || '',
                    ownerState: 1,
                },
            };
            fireEvents(
                'owner-htv-video-category-page',
                undefined,
                dyfComponentName
            );
        }
    }, []);

    const capitalizeYmm = (str: string) => {
        return str
            .split(' ')
            .map(e => e.charAt(0).toUpperCase() + e.slice(1))
            .join(' ');
    };
    useSeoConfig('htv-category-library', 'dyf', [
        {
            fieldName: 'ymm',
            fieldValue: capitalizeYmm(
                `${queryYear} ` +
                    capitalizeYmm(brand) +
                    ' ' +
                    capitalizeYmm(marketName?.data || '')
            ),
        },
        {
            fieldName: 'category-name',
            fieldValue: `${capitalizeYmm(category)}`,
        },
    ]);

    const getUpdatedCanonical = () => {
        const baseUrl = siteMapService.getBaseUrl(
            currentLanguageRegionCode,
            brand
        );
        if (
            categoryVideos &&
            Object.keys(categoryVideos?.data || '').length > 0
        ) {
            return baseUrl.domain
                .concat(baseUrl.root)
                .concat(
                    siteMapService
                        .getPathFromRouterConfigs(
                            serverContext.currentLanguageRegionCode,
                            serverContext.brand,
                            'HTVCategoryLibrary',
                            [queryModel, queryYear, queryCategory]
                        )
                        ?.toString() || ''
                );
        } else {
            return baseUrl.domain
                .concat(baseUrl.root)
                .concat(`/vehicle/${queryModel}/${queryYear}/`);
        }
    };
    serverContext.canonical = filterXSS(getUpdatedCanonical());

    useEffect(() => {
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
    }, []);

    const changeFirstCharToUpperCase = (serviceName: string) => {
        if (serviceName == undefined) return '';
        if (serviceName.toLowerCase() == 'fordpass') return 'FordPass';
        return serviceName.charAt(0).toUpperCase() + serviceName.slice(1);
    };

    const breadcrumbModelYear = useMemo(() => {
        if (currentLanguageRegionCode === 'fr-ca' && brand === 'lincoln') {
            return `${marketName?.data || ''} ${queryYear}`;
        } else return `${queryYear} ${marketName?.data || ''}`;
    }, [currentLanguageRegionCode, brand, marketName, queryYear]);

    const VideoCategoryPageBreadCrumb = () => {
        return howToVideoContent ? (
            <>
                <a
                    className="breadcrumb-link"
                    href={`${baseRoot}/how-to-videos/`}
                >
                    <span>
                        {howToVideoContent.howToVideosBreadcrumbHowToVideos}
                    </span>
                </a>
                <span className="back-slash">{'>'}</span>
                <a
                    className="breadcrumb-link"
                    href={`${baseRoot}/vehicle/${queryModel}/${queryYear}/how-to-videos/video-library/`}
                >
                    <span>{breadcrumbModelYear}</span>
                </a>
                <span className="back-slash">{'>'}</span>
            </>
        ) : (
            <></>
        );
    };

    const detailsBannerSubHeading = useMemo(() => {
        if (currentLanguageRegionCode === 'fr-ca' && brand === 'lincoln') {
            return `${brand} ${marketName?.data || ''} ${queryYear}`;
        } else return `${queryYear} ${brand} ${marketName?.data || ''}`;
    }, [currentLanguageRegionCode, brand, marketName, queryYear]);
    useFooterDisplay([howToVideoContent]);
    return (
        <>
            {howToVideoContent &&
                appInformationContent &&
                !categoryVideosResponseError && (
                    <div className="video-library-topic">
                        <div
                            className={`breadcrumbAccordion ${breadcrumbToggle &&
                                'expanded'}`}
                        >
                            <Breadcrumbs
                                currentPage={changeFirstCharToUpperCase(
                                    category
                                )}
                                breadcrumbName={
                                    howToVideoContent.howToVideosBreadcrumbSupport
                                }
                                type="common"
                            >
                                <VideoCategoryPageBreadCrumb />
                            </Breadcrumbs>

                            <div
                                onClick={() =>
                                    setBreadCrumbToggle(!breadcrumbToggle)
                                }
                            >
                                <FdsChevron
                                    direction={breadcrumbToggle ? 'up' : 'down'}
                                    type={'unfilled'}
                                />
                            </div>
                        </div>

                        <ViewTemplate
                            className="htv-category-video-library"
                            hideLinksList={true}
                            page={page.current}
                            ctaBottomComponent={
                                <>
                                    <div className="banner">
                                        <div className="htv-category-banner">
                                            <HowToBanner
                                                topComponent={
                                                    <DetailsBanner
                                                        title={
                                                            <>
                                                                <p className="subheading">
                                                                    {
                                                                        detailsBannerSubHeading
                                                                    }
                                                                </p>
                                                                <span className="title">
                                                                    {category ===
                                                                        'quick tips' && (
                                                                        <>
                                                                            {
                                                                                QUICKTIP_DYF_TITLE
                                                                            }{' '}
                                                                            <br />
                                                                        </>
                                                                    )}
                                                                    {category}
                                                                    <br />
                                                                    {category ===
                                                                    'quick tips'
                                                                        ? ''
                                                                        : howToVideoContent.htvLibrarylandingPageTitle.replace(
                                                                              '%vehicle%',
                                                                              ''
                                                                          )}
                                                                </span>
                                                            </>
                                                        }
                                                    />
                                                }
                                                backgroundImageDesktop={
                                                    process.env
                                                        .REACT_APP_AEM_BASE_URL +
                                                    howToVideoContent[
                                                        desktopBackground[
                                                            category
                                                                .replace(
                                                                    / /gm,
                                                                    ''
                                                                )
                                                                .toLowerCase()
                                                        ]
                                                            ? desktopBackground[
                                                                  category
                                                                      .replace(
                                                                          / /gm,
                                                                          ''
                                                                      )
                                                                      .toLowerCase()
                                                              ]
                                                            : 'htvBackgroundImageDesktop'
                                                    ]
                                                }
                                                backgroundImageMobile={
                                                    process.env
                                                        .REACT_APP_AEM_BASE_URL +
                                                    howToVideoContent[
                                                        mobileBackground[
                                                            category
                                                                .replace(
                                                                    / /gm,
                                                                    ''
                                                                )
                                                                .toLowerCase()
                                                        ]
                                                            ? mobileBackground[
                                                                  category
                                                                      .replace(
                                                                          / /gm,
                                                                          ''
                                                                      )
                                                                      .toLowerCase()
                                                              ]
                                                            : 'htvBackgroundImageMobile'
                                                    ]
                                                }
                                                bottomComponent={
                                                    dyfEligible?.eligibleCountdown && (
                                                        <CountDown
                                                            vehicleAttributes={{
                                                                year: parseInt(
                                                                    queryYear,
                                                                    10
                                                                ),
                                                                make: brand,
                                                                model:
                                                                    marketName?.data ||
                                                                    '',
                                                                ownerState: 1,
                                                            }}
                                                        />
                                                    )
                                                }
                                            />
                                            <div>
                                                {categoryVideos?.data &&
                                                howToVideoContent ? (
                                                    <HowToPopularVideoTiles
                                                        origin={
                                                            'category-videos'
                                                        }
                                                        className="category-video"
                                                        popularVideoItems={
                                                            categoryVideos.data
                                                        }
                                                        HeadTag={HeadTags.H2}
                                                    />
                                                ) : (
                                                    howToVideoContent &&
                                                    categoryVideosResponseError && (
                                                        <div
                                                            className="category-video-empty"
                                                            tabIndex={0}
                                                        >
                                                            {
                                                                howToVideoContent.videoPageErrorDescription
                                                            }
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            }
                            showEscapeHatch={false}
                        />
                    </div>
                )}
            {categoryVideosResponseError && <RoadBlockPage />}
        </>
    );
};
export default HTVCategoryLibrary;
